import { memo, useLayoutEffect } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { Stack, useNavigation, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "@src/components/View";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import { isWeb } from "@src/utils/web-helpers";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { EditUserForm, useEditUserFormState } from "@visioni/a3s-access/app";
import { useEditUserPage } from "@src/features/settings/hooks";

const EditUserScreen = memo(() => {
  const { userId } = useLocalSearchParams<{ userId: string }>();
  const { theme } = useUnistyles();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();

  const {
    userName,
    userEmail,
    isTenantAdmin,
    isSelf,
    siteList,
    cameraList,
    sitePermissions,
    cameraPermissions,
    isLoading,
    isPending,
    handleSubmit,
    handleCancel,
    handleRemoveAll,
  } = useEditUserPage(userId ?? "");

  const {
    tenantAdmin,
    siteSelection,
    cameraSelection,
    handleTenantAdminToggle,
    handleSiteToggle,
    handleSiteRoleChange,
    handleCameraToggle,
    handleCameraRoleChange,
    handleSubmit: handleFormSubmit,
  } = useEditUserFormState({
    isTenantAdmin,
    sitePermissions,
    cameraPermissions,
    onSubmit: handleSubmit,
  });

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Edit User",
      headerLeft,
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
    });
  }, [navigation, theme.colors.background, headerLeft]);

  if (isLoading) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <ActivityIndicator />
            </View>
          </WebLayout>
        </>
      );
    }
    return (
      <View style={styles.centered}>
        <ActivityIndicator />
      </View>
    );
  }

  if (isWeb) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            <View style={styles.formContainer}>
              <EditUserForm
                userName={userName}
                userEmail={userEmail}
                tenantAdmin={tenantAdmin}
                sites={siteList}
                cameras={cameraList}
                siteSelection={siteSelection}
                cameraSelection={cameraSelection}
                isPending={isPending}
                onTenantAdminToggle={handleTenantAdminToggle}
                onSiteToggle={handleSiteToggle}
                onSiteRoleChange={handleSiteRoleChange}
                onCameraToggle={handleCameraToggle}
                onCameraRoleChange={handleCameraRoleChange}
                onSubmit={handleFormSubmit}
                onCancel={handleCancel}
                onRemoveAll={isSelf ? undefined : handleRemoveAll}
              />
            </View>
          </ScrollView>
        </WebLayout>
      </>
    );
  }

  return (
    <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
      <EditUserForm
        userName={userName}
        userEmail={userEmail}
        tenantAdmin={tenantAdmin}
        sites={siteList}
        cameras={cameraList}
        siteSelection={siteSelection}
        cameraSelection={cameraSelection}
        isPending={isPending}
        onTenantAdminToggle={handleTenantAdminToggle}
        onSiteToggle={handleSiteToggle}
        onSiteRoleChange={handleSiteRoleChange}
        onCameraToggle={handleCameraToggle}
        onCameraRoleChange={handleCameraRoleChange}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        onRemoveAll={isSelf ? undefined : handleRemoveAll}
      />
    </ScrollView>
  );
});

export default EditUserScreen;

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
  },
  formContainer: {
    maxWidth: 800,
    width: "100%",
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
}));
